<template>
  <v-content fluid grid-list-lg style="padding: 0;">
      <v-layout v-if="dates" row wrap>
        <v-flex xs12>
          <v-subheader class="pl-0">Date Range</v-subheader>
          <small v-if="slowError" class="error--text">{{slowError}}</small>
        </v-flex><v-flex xs12 sm6>
          <datepick
            label="Start Date"
            v-model="q.time.$gte"
            :form.sync="startf"
            :max="finishf"
          />
        </v-flex><v-flex xs12 sm6>
          <datepick
            label="Finish Date"
            v-model="q.time.$lte"
            :form.sync="finishf"
            :min="startf"
          />
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex md2>
          <v-subheader class="pl-0">Field</v-subheader>
          <v-select
            :items="fields.filter(g =>
              (g.value !== 'studentId' || perm !== 'student')
              && (g.value !== 'flagIds' || showFlags)
            )"
            v-model="currField"
          />
        </v-flex><v-flex v-if="field.type === 'autocomp'" md10>
          <v-subheader class="pl-0">Value</v-subheader>
          <v-autocomplete
            :label="field.text"
            :items="items"
            v-model="q[currField].$in"
            multiple
            chips
            clearable
            deletable-chips
            small-chips
          />
        </v-flex><v-flex v-if="field.type === 'range'" md10>
          <v-subheader class="pl-0">Range</v-subheader>
          <v-range-slider
            :value="[q[currField].$gte * 100, q[currField].$lte * 100]"
            @input="
              q[currField].$gte = ($event[0] / 100).toFixed(2);
              q[currField].$lte = ($event[1] / 100).toFixed(2);
            "
            :max="100"
            :min="0"
            :step="5"
            thumb-label="always"
          />
        </v-flex><v-flex v-if="field.type === 'match'" md10>
          <v-subheader class="pl-0">Match</v-subheader>
          <v-text-field
            v-model="q[currField]"
          />
        </v-flex><v-flex xs12 v-if="filters.length">
          <v-subheader class="pl-0">Filters</v-subheader>
          <v-chip
            v-for="(filter, i) in filters"
            :key="i"
            close
            @input="filter.close()"
          >
            {{filter.name}}
          </v-chip>
        </v-flex>
      </v-layout>
  </v-content>
</template>

<script>
/* eslint-disable vue/no-side-effects-in-computed-properties */
import { mapGetters, mapState } from 'vuex';
import uniq from 'lodash/uniq';
import codes from '@/lib/teeth-codes';
import datepick from './datepick.vue';

const year = (new Date()).getFullYear();
const startyear = new Date(`${year}-01-01`);
const midyear = new Date(`${year}-06-30`);
const finishyear = new Date(`${year}-12-31`);
const period = Date.now() < midyear.getTime() ? 1 : 2;

const defQuery = {
  time: {
    $gte: period === 1 ? startyear : midyear,
    $lte: period === 1 ? midyear : finishyear,
  },
  flagIds: { $in: [] },
  grade: { $gte: 0, $lte: 1 },
  locId: { $in: [] },
  proIds: { $in: [] },
  stepIds: { $in: [] },
  studentId: { $in: [] },
  supervisorId: { $in: [] },
  teeth: { $in: [] },
  unitId: { $in: [] },
  stat: { $in: [] },
  patient: '',
};
const stats = [
  { text: 'In-progress', value: 'inprogress' },
  { text: 'Completed', value: 'complete' },
  { text: 'Exempt', value: 'exempt' },
];

export default {
  components: {
    datepick,
  },
  props: {
    dates: {
      type: Boolean,
      default: true,
    },
    stats: {
      type: Boolean,
      default: false,
    },
    flags: {
      type: Boolean,
      default: false,
    },
    query: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      startf: '',
      finishf: '',
      currField: 'unitId',
      fields: [
        { text: 'Grade', value: 'grade', type: 'range' },
        { text: 'Location', value: 'locId', type: 'autocomp' },
        { text: 'Patient', value: 'patient', type: 'match' },
        { text: 'Procedure', value: 'proIds', type: 'autocomp' },
        { text: 'Status', value: 'stat', type: 'autocomp' },
        { text: 'Step', value: 'stepIds', type: 'autocomp' },
        { text: 'Student', value: 'studentId', type: 'autocomp' },
        { text: 'Supervisor', value: 'supervisorId', type: 'autocomp' },
        { text: 'Tooth', value: 'teeth', type: 'autocomp' },
        { text: 'Unit', value: 'unitId', type: 'autocomp' },
        { text: 'Flag', value: 'flagIds', type: 'autocomp' },
      ],
      q: defQuery,
      lastUpdated: 0,
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('attempts', ['slowError']),
    ...mapGetters('flags', { flagsFind: 'find' }),
    ...mapGetters('locations', { locationsFind: 'find' }),
    ...mapGetters('procedures', { proceduresFind: 'find' }),
    ...mapGetters('steps', { stepsFind: 'find' }),
    ...mapGetters('units', { unitsFind: 'find' }),
    ...mapGetters('users', { usersFind: 'find', usersGet: 'get' }),
    perm() { return this.user && this.user.perm; },
    field() { return this.fields.find(f => f.value === this.currField); },
    items() {
      switch (this.currField) {
        case 'flagIds':
          return this.flagsFind({ query: { $sort: { name: 1 } } }).data
            .map(f => ({ ...f, value: f._id, text: f.name }));
        case 'locId':
          return this.locationsFind({ query: { $sort: { name: 1 } } }).data;
        case 'proIds':
          return this.proceduresFind({ query: { $sort: { name: 1 } } }).data;
        case 'stat':
          return stats;
        case 'stepIds':
          if (this.query.proIds && this.query.proIds.$in && this.query.proIds.$in.length) {
            const stepIds = uniq(this.proceduresFind({ query: { _id: this.query.proIds } }).data
              .reduce((a, p) => [...a, ...p.stepIds], []));
            return this.stepsFind({ query: { _id: { $in: stepIds }, $sort: { name: 1 } } }).data;
          }
          return this.stepsFind({ query: { $sort: { name: 1 } } }).data;
        case 'studentId':
          return this.usersFind({ query: { perm: 'student', $sort: { name: 1 } } }).data;
        case 'supervisorId':
          return this.usersFind({
            query: {
              perm: { $ne: 'student' },
              invisible: { $ne: true },
              $sort: { name: 1 },
            },
          }).data;
        case 'teeth':
          return codes.map(t => ({ text: t, value: t }));
        case 'unitId':
          return this.unitsFind({ query: { $sort: { year: -1, name: 1 } } }).data;
        default:
          return [];
      }
    },
    filters() {
      return [
        ...this.flagsFind({ query: { _id: this.q.flagIds, $sort: { name: 1 } } }).data
          .map(v => ({
            name: `Flag: ${v.name}`,
            close: () => { this.q.flagIds.$in = this.q.flagIds.$in.filter(id => id !== v._id); },
          })),
        ...(this.q.grade
          && (Number(this.q.grade.$gte || 0) !== 0 || Number(this.q.grade.$lte || 1) !== 1)
          ? [{
            name: `${Math.floor(Number(this.q.grade.$gte || 0) * 100)}% ≤ Grade ≤ ${Math.floor(Number(this.q.grade.$lte || 1) * 100)}%`,
            close: () => { this.q.grade.$gte = 0; this.q.grade.$lte = 1; },
          }]
          : []
        ),
        ...this.locationsFind({ query: { _id: this.q.locId, $sort: { name: 1 } } }).data
          .map(v => ({
            name: `Location: ${v.text}`,
            close: () => { this.q.locId.$in = this.q.locId.$in.filter(id => id !== v._id); },
          })),
        ...(this.q.patient ? [{
          name: `Patient: ${this.q.patient.replace(/[^\w- ]/g, '')}`,
          close: () => { this.q.patient = ''; },
        }] : []),
        ...this.proceduresFind({ query: { _id: this.q.proIds, $sort: { name: 1 } } }).data
          .map(v => ({
            name: `Procedure: ${v.text}`,
            close: () => { this.q.proIds.$in = this.q.proIds.$in.filter(id => id !== v._id); },
          })),
        ...(this.stats ? stats.filter(s => this.q.stat.$in.indexOf(s.value) !== -1)
          .map(v => ({
            name: `Status: ${v.text}`,
            close: () => { this.q.stat.$in = this.q.stat.$in.filter(s => s !== v.value); },
          })) : []),
        ...this.stepsFind({ query: { _id: this.q.stepIds, $sort: { name: 1 } } }).data
          .map(v => ({
            name: `Step: ${v.text}`,
            close: () => { this.q.stepIds.$in = this.q.stepIds.$in.filter(id => id !== v._id); },
          })),
        ...this.usersFind({ query: { _id: this.q.studentId, $sort: { name: 1 } } }).data
          .map(v => ({
            name: `Student: ${v.text}`,
            close: () => {
              this.q.studentId.$in = this.q.studentId.$in.filter(id => id !== v._id);
            },
          })),
        ...this.usersFind({ query: { _id: this.q.supervisorId, $sort: { name: 1 } } }).data
          .map(v => ({
            name: `Supervisor: ${v.text}`,
            close: () => {
              this.q.supervisorId.$in = this.q.supervisorId.$in.filter(id => id !== v._id);
            },
          })),
        ...this.q.teeth.$in.map(v => ({
          name: `Tooth: ${v}`,
          close: () => { this.q.teeth.$in = this.q.teeth.$in.filter(id => id !== v); },
        })),
        ...this.unitsFind({ query: { _id: this.q.unitId, $sort: { year: -1, name: 1 } } }).data
          .map(v => ({
            name: `Unit: ${v.text}`,
            close: () => { this.q.unitId.$in = this.q.unitId.$in.filter(id => id !== v._id); },
          })),
      ];
    },
    showFlags() { return this.flags && this.flagsFind().total; },
  },
  methods: {
    output() {
      if (Date.now() - this.lastUpdated < 100) return;
      this.lastUpdated = Date.now();
      const query = {};
      if (this.dates) query.time = this.q.time;
      if (!this.stats) query.stat = { $ne: 'inprogress' };
      this.fields.forEach((f) => {
        if (f.type === 'autocomp' && this.q[f.value].$in.length) {
          query[f.value] = this.q[f.value];
        } else if (
          f.type === 'range'
          && this.q[f.value]
          && (Number(this.q[f.value].$gte || 0) !== 0 || Number(this.q[f.value].$lte || 1) !== 1)) {
          query[f.value] = this.q[f.value];
          if (query[f.value].$gte) query[f.value].$gte = Number(query[f.value].$gte || 0);
          if (query[f.value].$lte) query[f.value].$lte = Number(query[f.value].$lte || 1);
        } else if (f.type === 'match' && this.q[f.value]) {
          query[f.value] = this.q[f.value];
        }
      });
      this.$emit('update:query', query);
    },
    input() {
      const query = {
        time: this.query.time || {
          $gte: period === 1 ? startyear : midyear,
          $lte: period === 1 ? midyear : finishyear,
        },
      };
      this.fields.forEach((f) => {
        if (f.type === 'autocomp') {
          query[f.value] = this.query[f.value] || { $in: [] };
        } else if (f.type === 'range' && (
          !this.query[f.value]
          || this.query[f.value].$gte !== 0
          || this.query[f.value].$lte !== 1
        )) {
          query[f.value] = this.query[f.value] || { $gte: 0, $lte: 1 };
        } else if (f.type === 'match') {
          query[f.value] = this.query[f.value] || '';
        }
      });
      return query;
    },
  },
  mounted() {
    if (this.perm === 'student') {
      this.fields = this.fields.filter(f => f.text !== 'Student');
    }
    if (!this.stats) {
      this.fields = this.fields.filter(f => f.text !== 'Status');
    }
    this.q = this.input();
  },
  watch: {
    q: {
      handler() {
        this.output();
      },
      deep: true,
    },
    query() {
      this.lastUpdated = Date.now();
      this.q = this.input();
    },
    filters(v) {
      this.$emit('update:filters', v);
    },
  },
};
</script>

<style lang="stylus">
.v-select.v-select--chips .v-select__selections
  min-height unset;
</style>
